import { DEFAULT_STEP_GOAL } from "./activity";
import { DOW_LABELS, addDays, parseDay } from "./date";
import { stepBreakdown } from "./energy";
import { isWeekend, windowDates } from "./foodTrends";
import { basedOn, type Insight, type Window } from "./insight";
import type { KayaState, WalkLog } from "./types";

/**
 * Steps and walks over a window, for Progress › Movement (ADR-033, research §2.4).
 *
 * A day's count comes only from `stepBreakdown` (invariant 6). A day with neither a phone reading
 * nor a typed count has no count — not 0 — and is left out of every average (invariant 11); a
 * treadmill estimate alone is not an all-day count either, so it does not make a day "measured".
 */

export const MIN_MEASURED_DAYS = 7;
export const MIN_WEEKEND_DAYS = 4;
export const MIN_CADENCE_WALKS = 3;
export const MIN_LEAD_WEEKS = 6;
export const LOW_STEPS = 5000;
/** Tudor-Locke: ~100 steps a minute is where walking becomes moderate intensity. */
export const MODERATE_CADENCE = 100;

export function stepGoal(S: KayaState): number {
  const g = S.profile.stepGoal;
  return typeof g === "number" && g > 0 ? g : DEFAULT_STEP_GOAL;
}

/** The day's steps, or null when nothing was measured or typed. */
export function dailySteps(S: KayaState, ds: string): number | null {
  const b = stepBreakdown(S, ds);
  if (!b.device && !b.manual) return null;
  return b.total;
}

interface Counted {
  ds: string;
  steps: number;
}

function counted(S: KayaState, dates: string[]): Counted[] {
  const out: Counted[] = [];
  for (const ds of dates) {
    const n = dailySteps(S, ds);
    if (n !== null) out.push({ ds, steps: n });
  }
  return out;
}

const avgOf = (xs: Counted[]): number | null =>
  xs.length ? Math.round(xs.reduce((a, x) => a + x.steps, 0) / xs.length) : null;

export interface StepsWindow {
  /** mean over measured days only */
  avg: number | null;
  measuredDays: number;
  of: number;
  overGoalDays: number;
  goal: number;
  /** measured days that carried a phone reading, for the "phone" tag */
  phoneDays: number;
}

export function stepsWindow(S: KayaState, today: string, w: Window): StepsWindow {
  const dates = windowDates(S, today, w);
  const c = counted(S, dates);
  const goal = stepGoal(S);
  return {
    avg: avgOf(c),
    measuredDays: c.length,
    of: dates.length,
    overGoalDays: c.filter((x) => x.steps >= goal).length,
    goal,
    phoneDays: c.filter((x) => (S.logs[x.ds]?.deviceSteps || 0) > 0).length,
  };
}

export interface VsPrevious {
  delta: number;
  avg: number;
  previous: number;
}

/** This window's average against the one before it; null for "All" or under 7 measured days on
 *  either side. The previous window is not clamped to the start date — a phone counts steps
 *  whether or not the plan had begun. */
export function vsPrevious(S: KayaState, today: string, w: Window): VsPrevious | null {
  if (w === 0) return null;
  const cur = stepsWindow(S, today, w);
  const prevEnd = addDays(today, -w);
  const prev = counted(S, Array.from({ length: w }, (_, i) => addDays(prevEnd, -(w - 1) + i)));
  const p = avgOf(prev);
  if (cur.avg === null || p === null || cur.measuredDays < MIN_MEASURED_DAYS || prev.length < MIN_MEASURED_DAYS)
    return null;
  return { delta: cur.avg - p, avg: cur.avg, previous: p };
}

export interface WeekdayWeekend {
  weekday: number | null;
  weekend: number | null;
  weekdayDays: number;
  weekendDays: number;
  /** weekend − weekday; null under the minimum on either side */
  diff: number | null;
}

export function weekdayWeekend(S: KayaState, today: string, w: Window): WeekdayWeekend {
  const c = counted(S, windowDates(S, today, w));
  const we = c.filter((x) => isWeekend(x.ds));
  const wd = c.filter((x) => !isWeekend(x.ds));
  const a = avgOf(wd);
  const b = avgOf(we);
  return {
    weekday: a,
    weekend: b,
    weekdayDays: wd.length,
    weekendDays: we.length,
    diff: a !== null && b !== null && we.length >= MIN_WEEKEND_DAYS && wd.length >= MIN_WEEKEND_DAYS ? b - a : null,
  };
}

export interface LowDay {
  ds: string;
  /** "Sun" */
  dow: string;
  steps: number;
  travel: boolean;
  prePeriod: boolean;
}

export interface LowDays {
  days: LowDay[];
  measured: number;
  of: number;
  travel: number;
  prePeriod: number;
}

/** Measured days under `under` steps. An unmeasured day is not a low day. */
export function lowDays(
  S: KayaState,
  today: string,
  w: Window,
  under = LOW_STEPS,
  prePeriod: (ds: string) => boolean = () => false
): LowDays {
  const dates = windowDates(S, today, w);
  const c = counted(S, dates);
  const days = c
    .filter((x) => x.steps < under)
    .map((x) => ({
      ds: x.ds,
      dow: DOW_LABELS[parseDay(x.ds).getDay()],
      steps: x.steps,
      travel: Boolean(S.travelDates?.[x.ds]),
      prePeriod: prePeriod(x.ds),
    }));
  return {
    days,
    measured: c.length,
    of: dates.length,
    travel: days.filter((d) => d.travel).length,
    prePeriod: days.filter((d) => d.prePeriod).length,
  };
}

/* ---------- walks ---------- */

export interface WalksWindow {
  count: number;
  /** summed over walks with a distance; null when none has one */
  km: number | null;
  kmWalks: number;
  avgKm: number | null;
  /** measured steps per walking minute, over walks with both; null when none has both */
  cadence: number | null;
  cadenceWalks: number;
}

/** A walk's distance: one the user vouched for first, then the one derived from measured steps. */
function walkKm(wk: WalkLog): number | null {
  if (typeof wk.kmDeclared === "number" && wk.kmDeclared > 0) return wk.kmDeclared;
  if (typeof wk.km === "number" && wk.km > 0) return wk.km;
  return null;
}

export function walksWindow(S: KayaState, today: string, w: Window): WalksWindow {
  const walks = windowDates(S, today, w).flatMap((ds) => S.logs[ds]?.walks || []);
  const kms = walks.map(walkKm).filter((k): k is number => k !== null);
  const timed = walks.filter((wk) => (wk.measuredSteps || 0) > 0 && wk.secs > 0);
  const steps = timed.reduce((a, wk) => a + (wk.measuredSteps || 0), 0);
  const mins = timed.reduce((a, wk) => a + wk.secs / 60, 0);
  const km = kms.length ? Math.round(kms.reduce((a, k) => a + k, 0) * 10) / 10 : null;
  return {
    count: walks.length,
    km,
    kmWalks: kms.length,
    avgKm: km !== null ? Math.round((km / kms.length) * 10) / 10 : null,
    cadence: timed.length && mins > 0 ? Math.round(steps / mins) : null,
    cadenceWalks: timed.length,
  };
}

/* ---------- steps as a lead ---------- */

export interface LeadWeek {
  /** the week's last day */
  end: string;
  avg: number;
  /** trend weight at the start less at the end; positive is a fall */
  fall: number;
}

export interface StepsLead {
  weeks: LeadWeek[];
  /** the two highest step weeks, best first */
  best: [LeadWeek, LeadWeek];
  /** the two weeks the trend fell fastest, fastest first */
  fastest: [LeadWeek, LeadWeek];
  /** the best step weeks are the fastest-falling ones */
  lead: boolean;
  /** weeks averaging at least the goal */
  overGoal: number;
}

function leadWeeks(S: KayaState, today: string, weights: (ds: string) => number | null): LeadWeek[] {
  const start = S.profile.startDate;
  const weeks: LeadWeek[] = [];
  for (let k = 0; k <= 520; k++) {
    const end = addDays(today, -7 * k);
    const first = addDays(end, -6);
    if (start && first < start) break;
    const c = counted(S, Array.from({ length: 7 }, (_, i) => addDays(first, i)));
    const a = weights(addDays(first, -1));
    const b = weights(end);
    if (c.length < 4 || a === null || b === null) continue;
    weeks.push({ end, avg: avgOf(c) ?? 0, fall: a - b });
  }
  return weeks.reverse();
}

/**
 * The two best step weeks against the two fastest trend-fall weeks, over whole seven-day blocks
 * ending today and going back to the start. A week counts with 4+ measured days and a trend
 * weight at both ends; null under `MIN_LEAD_WEEKS` such weeks. In n = 1 this is a pattern, not a
 * cause — the sentence says "in your data".
 */
export function stepsAsLead(
  S: KayaState,
  today: string,
  weights: (ds: string) => number | null
): StepsLead | null {
  const weeks = leadWeeks(S, today, weights);
  if (weeks.length < MIN_LEAD_WEEKS) return null;
  const byAvg = [...weeks].sort((x, y) => y.avg - x.avg);
  const byFall = [...weeks].sort((x, y) => y.fall - x.fall);
  const best: [LeadWeek, LeadWeek] = [byAvg[0], byAvg[1]];
  const fastest: [LeadWeek, LeadWeek] = [byFall[0], byFall[1]];
  const lead = best.every((b) => fastest.includes(b));
  return { weeks, best, fastest, lead, overGoal: weeks.filter((x) => x.avg >= stepGoal(S)).length };
}

/* ---------- the sentences ---------- */

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");
const NUM_WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];
const numWord = (n: number) => NUM_WORDS[n] ?? String(n);
const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);
const more = (n: number, what: string) => `after ${n} more ${n === 1 ? what : `${what}s`}`;
const FULL_DOW = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export interface MovementDeps {
  prePeriod: (ds: string) => boolean;
  cycleOn: boolean;
  weights: (ds: string) => number | null;
}

export function movementInsights(S: KayaState, today: string, w: Window, deps: MovementDeps): Insight[] {
  const out: Insight[] = [];
  const sw = stepsWindow(S, today, w);
  const measuredBasis = (n: number) => `${n} measured ${plural(n, "day")}`;
  const short = sw.measuredDays < MIN_MEASURED_DAYS;
  const waitDays = more(MIN_MEASURED_DAYS - sw.measuredDays, "measured day");
  const phone = sw.phoneDays > 0 ? { tag: "phone" as const } : {};

  // M1
  if (short || sw.avg === null) {
    out.push({ key: "move.avg", text: "The step average appears after 7 measured days.", basedOn: basedOn(measuredBasis(sw.measuredDays)), wait: waitDays });
  } else {
    let text = `Over the goal on ${sw.overGoalDays} of ${sw.measuredDays} measured days; `;
    const gap = Math.round((sw.goal - sw.avg) / 10) * 10;
    if (gap > 0) {
      // Her own pace when a walk has measured it, else the moderate-intensity threshold.
      const cadence = walksWindow(S, today, 0).cadence ?? MODERATE_CADENCE;
      const mins = Math.max(1, Math.ceil(gap / cadence));
      text += `the average is ${fmt(gap)} short, which is one ${mins}-minute walk.`;
    } else if (gap < 0) text += `the average clears it by ${fmt(-gap)}.`;
    else text += "the average sits on it.";
    out.push({ key: "move.avg", text, basedOn: basedOn(measuredBasis(sw.measuredDays)), ...phone });
  }

  // M2
  const ww = weekdayWeekend(S, today, w);
  if (ww.diff === null || ww.weekday === null || ww.weekend === null) {
    const need = Math.max(MIN_WEEKEND_DAYS - ww.weekendDays, MIN_WEEKEND_DAYS - ww.weekdayDays, 1);
    out.push({
      key: "move.weekend",
      text: "Weekdays against weekends appear after 4 measured days of each.",
      basedOn: basedOn(`${ww.weekdayDays} ${plural(ww.weekdayDays, "weekday")}`, `${ww.weekendDays} weekend ${plural(ww.weekendDays, "day")} measured`),
      wait: more(need, ww.weekendDays < MIN_WEEKEND_DAYS ? "measured weekend day" : "measured weekday"),
    });
  } else {
    const d = Math.round(ww.diff / 100) * 100;
    let text: string;
    if (Math.abs(d) < 300) text = "Weekdays and weekends run within 300 steps of each other.";
    else if (d < 0) {
      text = `Weekends run ${fmt(-d)} below weekdays`;
      if (ww.weekend < sw.goal && ww.weekday >= sw.goal) text += " — that is where the goal is missed.";
      else if (ww.weekday < sw.goal) text += "; both sit under the goal.";
      else text += "; both clear the goal.";
    } else {
      text = `Weekends run ${fmt(d)} above weekdays`;
      text += ww.weekday < sw.goal && ww.weekend >= sw.goal ? " — the weekdays are where the goal is missed." : ".";
    }
    out.push({
      key: "move.weekend",
      text,
      basedOn: basedOn(`${ww.weekdayDays} ${plural(ww.weekdayDays, "weekday")}`, `${ww.weekendDays} weekend ${plural(ww.weekendDays, "day")} measured`),
    });
  }

  // M3
  const pre = (ds: string) => deps.cycleOn && deps.prePeriod(ds);
  const low = lowDays(S, today, w, LOW_STEPS, pre);
  if (short) {
    out.push({ key: "move.low", text: `Days under ${fmt(LOW_STEPS)} steps appear after 7 measured days.`, basedOn: basedOn(measuredBasis(low.measured)), wait: waitDays });
  } else {
    const n = low.days.length;
    let text: string;
    if (n === 0) text = `No day under ${fmt(LOW_STEPS)} steps in ${low.of} days.`;
    else {
      text = `${n} of ${low.of} days under ${fmt(LOW_STEPS)} steps`;
      const home = low.days.filter((d) => !d.travel);
      const dows = new Set(home.map((d) => parseDay(d.ds).getDay()));
      const tail = low.travel ? " or travel days" : "";
      if (n === 1) text += `, a ${low.travel ? "travel day" : FULL_DOW[parseDay(low.days[0].ds).getDay()]}.`;
      else if (!home.length) text += ", all travel days.";
      else if (dows.size === 1) text += `, all ${FULL_DOW[[...dows][0]]}s${tail}.`;
      else if (home.every((d) => isWeekend(d.ds))) text += `, all weekends${tail}.`;
      else text += ".";
      if (low.prePeriod > 0) {
        const pd = low.days.filter((d) => d.prePeriod);
        const span = pd.every((d) => isWeekend(d.ds)) ? "weekend" : "week";
        text += ` ${numWord(low.prePeriod)} ${low.prePeriod === 1 ? "was" : "were"} the pre-period ${span}.`;
      }
    }
    out.push({
      key: "move.low",
      text,
      basedOn: basedOn(measuredBasis(low.measured)),
      ...(low.prePeriod > 0 ? { tag: "cycle" as const, link: { to: "/progress/cycle", label: "See Cycle" } } : {}),
    });
  }

  // M5
  const wk = walksWindow(S, today, w);
  const training = { to: "/progress/training", label: "See Training" };
  if (wk.cadence === null || wk.cadenceWalks < MIN_CADENCE_WALKS) {
    out.push({
      key: "move.walks",
      text: "Walking cadence appears after 3 walks with measured steps.",
      basedOn: basedOn(`${wk.cadenceWalks} ${plural(wk.cadenceWalks, "walk")} with measured steps`),
      tag: "phone",
      wait: more(MIN_CADENCE_WALKS - wk.cadenceWalks, "measured walk"),
      link: training,
    });
  } else {
    const text =
      wk.cadence >= MODERATE_CADENCE
        ? `Your walking cadence is ~${wk.cadence} steps a minute — brisk starts at about ${MODERATE_CADENCE}, so these count as moderate activity.`
        : `Your walking cadence is ~${wk.cadence} steps a minute — brisk starts at about ${MODERATE_CADENCE}, so a slightly quicker step would make these moderate activity.`;
    out.push({ key: "move.walks", text, basedOn: basedOn(`${wk.cadenceWalks} walks with measured steps`), tag: "phone", link: training });
  }

  // M4 — over the whole record, whatever the window: a month holds four weeks.
  const lead = stepsAsLead(S, today, deps.weights);
  const energy = { to: "/progress/energy", label: "See Energy" };
  if (!lead) {
    const have = leadWeeks(S, today, deps.weights).length;
    out.push({
      key: "move.lead",
      text: "Steps against the scale appear after 6 weeks of steps and weigh-ins.",
      basedOn: basedOn(`${have} ${plural(have, "week")} of steps and weigh-ins`),
      wait: more(MIN_LEAD_WEEKS - have, "week"),
      link: energy,
    });
  } else {
    const goalPhrase = `${fmt(sw.goal)}+`;
    const text = lead.lead
      ? lead.overGoal === 2 && lead.best.every((b) => b.avg >= sw.goal)
        ? `The two weeks that averaged ${goalPhrase} were the two the trend fell fastest. Steps are the lever that shows up on the scale first.`
        : "Your two best step weeks were the two the trend fell fastest. Steps are the lever that shows up on the scale first."
      : "Your two best step weeks were not the two the trend fell fastest; in your data, steps alone do not explain the scale.";
    out.push({
      key: "move.lead",
      text,
      basedOn: basedOn(`${lead.weeks.length} weeks of steps and weigh-ins`, "in your data"),
      link: energy,
    });
  }

  return out;
}
